import { ref, push, set, update, remove, onValue, get } from 'firebase/database'
import { db, tref } from './firebase.js'
import { logActivity } from './activity.js'

// Inventory / Products. /club/products. Each product carries an embedded
// history log of stock/price events.
//   {id}: { sku, barcode, brand, vendorId, category, department, size, color,
//           cost, retail, margin, quantity, minQuantity, reorderQuantity,
//           images, notes, active, history:{}, createdAt }
const BASE = 'club/products'
const num = (v) => (v === '' || v == null ? 0 : Number(v) || 0)
export const marginPct = (cost, retail) => (num(retail) > 0 ? Math.round(((num(retail) - num(cost)) / num(retail)) * 1000) / 10 : 0)
export const isLow = (p) => Number(p.quantity) <= Number(p.minQuantity || 0) && Number(p.minQuantity || 0) > 0

export function watchProducts(cb) { return onValue(tref(BASE), (s) => cb(s.val() || {})) }
export async function getProductsOnce() { return (await get(tref(BASE))).val() || {} }

function histEntry(type, note, extra = {}) {
  return { ts: Date.now(), type, note: String(note || ''), ...extra }
}

export async function createProduct(d) {
  const name = String(d.name || d.sku || '').trim()
  if (!name && !d.sku) throw new Error('Product name or SKU is required.')
  const node = push(tref(BASE))
  const cost = num(d.cost), retail = num(d.retail)
  await set(node, {
    id: node.key, name, sku: String(d.sku || '').trim(), barcode: String(d.barcode || '').trim(),
    brand: String(d.brand || '').trim(), vendorId: d.vendorId || null,
    category: String(d.category || '').trim(), department: String(d.department || '').trim(),
    size: String(d.size || '').trim(), color: String(d.color || '').trim(),
    cost, retail, margin: marginPct(cost, retail),
    quantity: num(d.quantity), minQuantity: num(d.minQuantity), reorderQuantity: num(d.reorderQuantity),
    images: String(d.images || '').trim(), notes: String(d.notes || '').trim(),
    active: d.active !== false,
    history: { [push(tref(`${BASE}/${node.key}/history`)).key]: histEntry('created', `Initial qty ${num(d.quantity)}`) },
    createdAt: Date.now(),
  })
  logActivity({ type: 'inventory', icon: '🏷️', text: `Product added: ${name || d.sku}` })
  return node.key
}

export async function updateProduct(id, patch, prev) {
  const clean = {}
  for (const k of ['name', 'sku', 'barcode', 'brand', 'category', 'department', 'size', 'color', 'images', 'notes']) if (patch[k] != null) clean[k] = String(patch[k]).trim()
  if (patch.vendorId !== undefined) clean.vendorId = patch.vendorId || null
  if (patch.active !== undefined) clean.active = !!patch.active
  for (const k of ['minQuantity', 'reorderQuantity']) if (patch[k] !== undefined) clean[k] = num(patch[k])
  if (patch.cost !== undefined || patch.retail !== undefined) {
    const cost = patch.cost !== undefined ? num(patch.cost) : num(prev?.cost)
    const retail = patch.retail !== undefined ? num(patch.retail) : num(prev?.retail)
    clean.cost = cost; clean.retail = retail; clean.margin = marginPct(cost, retail)
    const hk = push(tref(`${BASE}/${id}/history`)).key
    clean[`history/${hk}`] = histEntry('price', `Cost ${cost}, Retail ${retail}`)
  }
  await update(tref(`${BASE}/${id}`), clean)
}

// Stock movements — receive (+), adjust (±), or absolute count.
export async function receiveStock(id, qty, note, cur) {
  const q = num(qty); const newQ = num(cur) + q
  const hk = push(tref(`${BASE}/${id}/history`)).key
  await update(tref(`${BASE}/${id}`), { quantity: newQ, [`history/${hk}`]: histEntry('receive', note, { delta: q }) })
  logActivity({ type: 'inventory', icon: '📦', text: `Received ${q} → ${newQ}` })
}
export async function adjustStock(id, delta, note, cur) {
  const d = num(delta); const newQ = num(cur) + d
  const hk = push(tref(`${BASE}/${id}/history`)).key
  await update(tref(`${BASE}/${id}`), { quantity: newQ, [`history/${hk}`]: histEntry('adjust', note, { delta: d }) })
}
export async function setCount(id, newQty, note, cur) {
  const nq = num(newQty); const delta = nq - num(cur)
  const hk = push(tref(`${BASE}/${id}/history`)).key
  await update(tref(`${BASE}/${id}`), { quantity: nq, [`history/${hk}`]: histEntry('count', note, { delta }) })
}
// Sale decrement — used by POS.
export async function sellStock(id, qty, cur) {
  const newQ = num(cur) - num(qty)
  const hk = push(tref(`${BASE}/${id}/history`)).key
  await update(tref(`${BASE}/${id}`), { quantity: newQ, [`history/${hk}`]: histEntry('sale', `Sold ${num(qty)}`, { delta: -num(qty) }) })
}

export async function duplicateProduct(id, overrides, prev) {
  const base = prev || {}
  return createProduct({ ...base, id: undefined, history: undefined, quantity: 0, ...overrides })
}

export async function deleteProduct(id) { await remove(tref(`${BASE}/${id}`)) }
